/**
 * Tilt Race — marble physics + race state.
 *
 * Top-down arena. Each player has a marble that accelerates in the
 * direction of their phone's tilt (joyL angle), scaled by joyL-power
 * (continuous 0..1 from the accelerometer). The first marble to cross
 * the finish line at the far end wins; after a short celebration the
 * arena resets and a new race begins.
 *
 * Hazards: a few pit zones return the marble to its lane's start.
 * Walls bound the arena and create a chicane partway down.
 */

export const ARENA_W = 1800;
export const ARENA_H = 900;
export const MARBLE_RADIUS = 22;
export const TILT_ACCEL = 1100;        // px/s² at full tilt magnitude
export const BOOST_ACCEL = 2400;       // px/s² added while A held + boost charged
export const DAMPING = 1.6;            // friction coefficient (per second)
export const MAX_SPEED = 520;
export const BOOST_DURATION_S = 0.6;
export const BOOST_COOLDOWN_S = 5;
export const ROUND_END_DELAY_S = 4;
export const COUNTDOWN_S = 3.2;
export const MAX_PLAYERS = 4;

export const PLAYER_COLOURS = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a'];

/** Spawn lanes — left edge, vertically stacked by slot. */
const LANE_STARTS: Array<{ x: number; y: number }> = [
  { x: 120, y: 160 },
  { x: 120, y: 360 },
  { x: 120, y: 560 },
  { x: 120, y: 760 },
];

/** Finish line is the rightmost ~5% of the arena. */
export const FINISH_X = ARENA_W - 60;

/** Walls — a chicane around the middle. Designed so no single corridor
 *  reaches the finish, players have to commit to a side. */
export const WALLS: ReadonlyArray<{ x: number; y: number; w: number; h: number }> = [
  // Two staggered slabs near the middle
  { x: 600,  y: 0,   w: 26, h: 540 },
  { x: 880,  y: 380, w: 26, h: 520 },
  { x: 1160, y: 0,   w: 26, h: 460 },
  // Outer-edge "fences" that pinch the run-up to the finish line
  { x: 1500, y: 240, w: 130, h: 26 },
  { x: 1500, y: 640, w: 130, h: 26 },
];

/** Pit zones — falling in resets the marble to its lane start and zeros
 *  its velocity. Slim circles tucked into the chicane gaps. */
export const PITS: ReadonlyArray<{ x: number; y: number; r: number }> = [
  { x: 740,  y: 600, r: 46 },
  { x: 1020, y: 220, r: 46 },
  { x: 1340, y: 520, r: 46 },
];

export interface Marble {
  p: number;
  colour: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Latest joyL heading from the phone (radians). */
  inHeading: number;
  /** Latest joyL-power magnitude 0..1 (1 = full tilt). */
  inPower: number;
  /** True while phone is past the deadzone. */
  inThrust: boolean;
  /** game-time of next allowed boost. */
  boostCdUntil: number;
  /** game-time boost effect ends. */
  boostUntil: number;
  score: number;
  finishedAt: number | null;
  /** True for host-side bots filling otherwise-empty slots. */
  ai: boolean;
  /** Per-bot racing personality. 'racer' cuts tight + boosts often,
   *  'careful' takes wide margins + boosts only when totally clear,
   *  'opportunist' falls between with a slipstream tendency. */
  aiPersonality: 'racer' | 'careful' | 'opportunist';
  /** Recent x progress sampler — used to detect stuck-against-wall
   *  states so we can re-pick a waypoint. Refreshed every ~0.5s. */
  aiLastProgressX: number;
  aiLastProgressAt: number;
  /** Cached chosen vertical corridor for the next wall band — sticky
   *  for a window after a decision so the bot commits and doesn't
   *  oscillate over the chicane line. */
  aiCommittedY: number;
  aiCommittedUntil: number;
}

export type RaceState =
  | { kind: 'lobby' }
  | { kind: 'countdown'; startedAt: number }
  | { kind: 'racing'; startedAt: number }
  | { kind: 'over'; winnerP: number | null; endedAt: number };

export interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  size: number; colour: string;
}

export interface Shake {
  intensity: number; duration: number; startedAt: number;
}

export interface World {
  marbles: Map<number, Marble>;
  race: RaceState;
  time: number;
  particles: Particle[];
  shake: Shake | null;
  /** Game-time the world last entered lobby. */
  lobbyEnteredAt: number;
  /** True while a bot-only demo race is playing because the room is empty. */
  attractMode: boolean;
}

export const ATTRACT_DELAY = 15.0;

export function newWorld(): World {
  return {
    marbles: new Map(),
    race: { kind: 'lobby' },
    time: 0,
    particles: [],
    shake: null,
    lobbyEnteredAt: 0,
    attractMode: false,
  };
}

function spawnParticles(world: World, opts: {
  x: number; y: number; count: number;
  speed: number; spread?: number;
  life: number; size: number; colour: string;
  baseHeading?: number;
}): void {
  const baseH = opts.baseHeading ?? null;
  const spread = opts.spread ?? Math.PI * 2;
  for (let i = 0; i < opts.count; i++) {
    const heading = baseH !== null
      ? baseH + (Math.random() - 0.5) * spread
      : Math.random() * Math.PI * 2;
    const speed = opts.speed * (0.5 + Math.random() * 0.5);
    const life = opts.life * (0.7 + Math.random() * 0.6);
    world.particles.push({
      x: opts.x, y: opts.y,
      vx: Math.cos(heading) * speed,
      vy: Math.sin(heading) * speed,
      life, maxLife: life,
      size: opts.size * (0.7 + Math.random() * 0.6),
      colour: opts.colour,
    });
  }
}

export function triggerShake(world: World, intensity: number, duration: number): void {
  if (!world.shake) { world.shake = { intensity, duration, startedAt: world.time }; return; }
  const elapsed = world.time - world.shake.startedAt;
  const remaining = Math.max(0, world.shake.intensity * (1 - elapsed / world.shake.duration));
  if (intensity > remaining) world.shake = { intensity, duration, startedAt: world.time };
}

export function addMarble(world: World, p: number, ai = false): Marble {
  let m = world.marbles.get(p);
  if (m) return m;
  const start = LANE_STARTS[p % LANE_STARTS.length]!;
  m = {
    p,
    colour: PLAYER_COLOURS[p % PLAYER_COLOURS.length]!,
    x: start.x,
    y: start.y,
    vx: 0, vy: 0,
    inHeading: 0,
    inPower: 0,
    inThrust: false,
    boostCdUntil: 0,
    boostUntil: 0,
    score: 0,
    finishedAt: null,
    ai,
    aiPersonality: pickPersonality(),
    aiLastProgressX: start.x,
    aiLastProgressAt: 0,
    aiCommittedY: start.y,
    aiCommittedUntil: 0,
  };
  world.marbles.set(p, m);
  return m;
}

/** Top up empty slots with AI marbles so the screen is never an empty
 *  lobby. Bots race east-bound, dodging pits + walls. */
export function autoFillAI(world: World): void {
  for (let p = 0; p < MAX_PLAYERS; p++) {
    if (!world.marbles.has(p)) addMarble(world, p, true);
  }
}

export function removeMarble(world: World, p: number): void {
  world.marbles.delete(p);
  if (world.marbles.size === 0 && world.race.kind !== 'lobby') {
    world.race = { kind: 'lobby' };
  }
}

export function applyInput(world: World, p: number, slot: string, value: string): void {
  const m = world.marbles.get(p);
  if (!m) return;
  // Any human signal while we're in lobby starts the race. Tilt or
  // boost-button press both count.
  if (world.race.kind === 'lobby' && !m.ai) {
    if ((slot === 'A' || slot === 'joyL-thrust') && value === '1'
      || slot === 'joyL-power' && Number(value) > 0) {
      tryStartRace(world);
      return;
    }
  }
  if (slot === 'joyL') {
    const a = parseInt(value, 10) / 1000;
    if (Number.isFinite(a)) m.inHeading = a;
    return;
  }
  if (slot === 'joyL-thrust') {
    m.inThrust = value === '1';
    if (!m.inThrust) m.inPower = 0;
    return;
  }
  if (slot === 'joyL-power') {
    const p1 = parseInt(value, 10) / 1000;
    if (Number.isFinite(p1)) m.inPower = clamp(p1, 0, 1);
    return;
  }
  if (slot === 'joyL-end') {
    m.inThrust = false;
    m.inPower = 0;
    return;
  }
  if (slot === 'A') {
    if (value === '1') tryBoost(world, m);
    return;
  }
}

function tryBoost(world: World, m: Marble): void {
  if (world.time < m.boostCdUntil) return;
  m.boostUntil = world.time + BOOST_DURATION_S;
  m.boostCdUntil = world.time + BOOST_COOLDOWN_S;
}

export function humanCount(world: World): number {
  let n = 0;
  for (const m of world.marbles.values()) if (!m.ai) n += 1;
  return n;
}

/** Called when a human pairs + signals ready. Fills bot marbles in,
 *  starts the countdown. The race transitions to 'racing' once the
 *  countdown fires. */
export function tryStartRace(world: World): void {
  if (world.race.kind !== 'lobby') return;
  if (humanCount(world) === 0) return;
  autoFillAI(world);
  for (const m of world.marbles.values()) resetMarble(m);
  world.race = { kind: 'countdown', startedAt: world.time };
}

/** Drop bots, return to a clean lobby. */
export function returnToLobby(world: World): void {
  for (const [p, m] of [...world.marbles.entries()]) {
    if (m.ai) world.marbles.delete(p);
  }
  world.particles.length = 0;
  world.shake = null;
  world.race = { kind: 'lobby' };
  world.lobbyEnteredAt = world.time;
}

function resetMarble(m: Marble): void {
  const start = LANE_STARTS[m.p % LANE_STARTS.length]!;
  m.x = start.x; m.y = start.y;
  m.vx = 0; m.vy = 0;
  m.finishedAt = null;
}

export function step(world: World, dt: number): void {
  world.time += dt;

  // Attract-mode kick-out — a human just paired into a bot-only demo.
  // Drop the bots and return to lobby so the human can fire a real race.
  if (world.attractMode && humanCount(world) > 0) {
    for (const m of [...world.marbles.values()]) {
      if (m.ai) world.marbles.delete(m.p);
    }
    world.particles.length = 0;
    world.shake = null;
    world.race = { kind: 'lobby' };
    world.attractMode = false;
    world.lobbyEnteredAt = world.time;
  }
  // Attract-mode kick-in — empty lobby for ATTRACT_DELAY seconds:
  // autoFillAI and start a demo race.
  if (
    world.race.kind === 'lobby'
    && world.marbles.size === 0
    && !world.attractMode
    && world.time - world.lobbyEnteredAt >= ATTRACT_DELAY
  ) {
    autoFillAI(world);
    world.attractMode = true;
    world.race = { kind: 'countdown', startedAt: world.time };
  }

  // During countdown the marbles sit still. We zero velocities each
  // frame but PRESERVE inThrust / inPower / inHeading so a player who
  // is already holding a tilt has their input live the instant GO
  // fires. Clearing them on every countdown step was the cause of the
  // 'second game doesn't move' bug: the pad only sends a thrust=1 on
  // the leading edge of tilt, so once the host cleared the field the
  // pad never re-sent it.
  if (world.race.kind === 'countdown') {
    for (const m of world.marbles.values()) {
      m.vx = 0; m.vy = 0;
    }
    if (world.time - world.race.startedAt >= COUNTDOWN_S) {
      world.race = { kind: 'racing', startedAt: world.time };
    }
    return;
  }
  // AI tick first so bots have their inputs ready for the physics pass.
  for (const m of world.marbles.values()) {
    if (!m.ai) continue;
    if (m.finishedAt !== null) continue;
    aiSteer(world, m);
  }
  for (const m of world.marbles.values()) {
    if (m.finishedAt !== null) continue;
    // Apply tilt acceleration if the player is past the deadzone.
    if (m.inThrust) {
      const accel = TILT_ACCEL * m.inPower;
      m.vx += Math.cos(m.inHeading) * accel * dt;
      m.vy += Math.sin(m.inHeading) * accel * dt;
    }
    if (world.time < m.boostUntil) {
      m.vx += Math.cos(m.inHeading) * BOOST_ACCEL * dt;
      m.vy += Math.sin(m.inHeading) * BOOST_ACCEL * dt;
    }
    // Damp velocity each tick (frame-rate independent exponential).
    const damp = Math.exp(-DAMPING * dt);
    m.vx *= damp; m.vy *= damp;
    // Cap speed.
    const sp = Math.hypot(m.vx, m.vy);
    if (sp > MAX_SPEED) { const k = MAX_SPEED / sp; m.vx *= k; m.vy *= k; }
    // Tentative new position, then resolve collisions axis-wise so the
    // marble slides along walls instead of dead-stopping.
    let nx = m.x + m.vx * dt;
    let ny = m.y + m.vy * dt;
    const preSpeed = Math.hypot(m.vx, m.vy);
    let bouncedX = false, bouncedY = false;
    if (!canPlace(nx, m.y)) { m.vx = -m.vx * 0.3; nx = m.x; bouncedX = true; }
    if (!canPlace(nx, ny))  { m.vy = -m.vy * 0.3; ny = m.y; bouncedY = true; }
    m.x = nx; m.y = ny;
    // Wall thud — only fire on a meaningful pre-bounce speed so a
    // slow drift along a wall doesn't sparkle constantly.
    if ((bouncedX || bouncedY) && preSpeed > 220) {
      spawnParticles(world, {
        x: m.x, y: m.y, count: 6,
        speed: 200, life: 0.20, size: 2,
        colour: 'rgba(207,216,255,0.85)',
      });
    }
    // Pit detection — drop in => reset to lane, swirl down particles.
    for (const pit of PITS) {
      const dx = m.x - pit.x, dy = m.y - pit.y;
      if (dx * dx + dy * dy < pit.r * pit.r) {
        spawnParticles(world, {
          x: m.x, y: m.y, count: 14,
          speed: 220, life: 0.55, size: 3,
          colour: 'rgba(180,140,255,0.9)',
        });
        resetMarble(m);
        break;
      }
    }
    // Finish line check (right side of arena).
    if (m.x > FINISH_X && world.race.kind === 'racing' && m.finishedAt === null) {
      m.finishedAt = world.time;
      // Confetti burst in the marble's colour + a bright white core.
      spawnParticles(world, {
        x: m.x, y: m.y, count: 30,
        speed: 420, life: 0.80, size: 4,
        colour: m.colour,
      });
      spawnParticles(world, {
        x: m.x, y: m.y, count: 16,
        speed: 320, life: 0.95, size: 3,
        colour: '#fff',
      });
      triggerShake(world, 9, 0.32);
    }
  }

  // Marble-vs-marble collisions — elastic bump along the contact
  // normal. Adds real interaction: you can shoulder a leader off line,
  // bots can be passed by ramming, finishes get scrappy. Skipped for
  // finished marbles so the post-finish coast doesn't shove anyone.
  const marbleArr = [...world.marbles.values()];
  for (let i = 0; i < marbleArr.length; i++) {
    for (let j = i + 1; j < marbleArr.length; j++) {
      const a = marbleArr[i]!, b = marbleArr[j]!;
      if (a.finishedAt !== null || b.finishedAt !== null) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.hypot(dx, dy);
      const minD = MARBLE_RADIUS * 2 + 1;
      if (d >= minD || d <= 0.01) continue;
      // Separate so they no longer overlap.
      const nx = dx / d;
      const ny = dy / d;
      const overlap = (minD - d) * 0.5;
      a.x -= nx * overlap; a.y -= ny * overlap;
      b.x += nx * overlap; b.y += ny * overlap;
      // Trade momentum along the contact normal (equal mass elastic).
      const va = a.vx * nx + a.vy * ny;
      const vb = b.vx * nx + b.vy * ny;
      const dv = vb - va;
      const ELASTICITY = 0.85;
      a.vx += nx * dv * ELASTICITY;
      a.vy += ny * dv * ELASTICITY;
      b.vx -= nx * dv * ELASTICITY;
      b.vy -= ny * dv * ELASTICITY;
      // Sparks at the contact point if either was moving fast.
      const speedSum = Math.hypot(a.vx, a.vy) + Math.hypot(b.vx, b.vy);
      if (speedSum > 200) {
        spawnParticles(world, {
          x: (a.x + b.x) / 2, y: (a.y + b.y) / 2,
          count: 5, speed: 180, life: 0.18, size: 2,
          colour: '#ffd84a',
        });
        triggerShake(world, 2.5, 0.16);
      }
    }
  }

  // Particles tick.
  for (let i = world.particles.length - 1; i >= 0; i--) {
    const p = world.particles[i]!;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vx *= 0.985; p.vy *= 0.985;
    p.life -= dt;
    if (p.life <= 0) world.particles.splice(i, 1);
  }
  if (world.shake && world.time - world.shake.startedAt >= world.shake.duration) {
    world.shake = null;
  }
  if (world.race.kind === 'racing') {
    const allFinished = [...world.marbles.values()].every((m) => m.finishedAt !== null);
    const someoneFinished = [...world.marbles.values()].some((m) => m.finishedAt !== null);
    if (allFinished || (someoneFinished && allOthersStalled(world))) {
      const winner = leaderboardThisRace(world)[0];
      if (winner) winner.score += 1;
      world.race = { kind: 'over', winnerP: winner?.p ?? null, endedAt: world.time };
    }
  } else if (world.race.kind === 'over') {
    if (world.time - world.race.endedAt >= ROUND_END_DELAY_S) {
      if (humanCount(world) === 0 && !world.attractMode) {
        // No humans — drop bots, idle in lobby.
        returnToLobby(world);
        return;
      }
      // Reset for a fresh race; bots and humans (and attract-mode bots)
      // stay in place. Run the countdown again so the start feels
      // deliberate.
      for (const m of world.marbles.values()) resetMarble(m);
      world.race = { kind: 'countdown', startedAt: world.time };
    }
  }
}

/** Once at least one marble has finished, give the others a short
 *  window to also cross; if nobody else moves, end the race anyway
 *  rather than holding indefinitely on an AFK player. */
function allOthersStalled(world: World): boolean {
  // 6s after first finish, end the race.
  const finishers = [...world.marbles.values()].filter((m) => m.finishedAt !== null);
  if (finishers.length === 0) return false;
  const earliest = Math.min(...finishers.map((m) => m.finishedAt!));
  return world.time - earliest > 6;
}

/** Ordered fastest-first. Players who haven't finished sort last by
 *  current x (closer to the finish wins the tiebreaker). */
export function leaderboardThisRace(world: World): Marble[] {
  return [...world.marbles.values()].sort((a, b) => {
    if (a.finishedAt !== null && b.finishedAt !== null) return a.finishedAt - b.finishedAt;
    if (a.finishedAt !== null) return -1;
    if (b.finishedAt !== null) return 1;
    return b.x - a.x;
  });
}

/** Bot personality presets — tuned constants per style. */
const AI_PROFILES: Record<Marble['aiPersonality'], {
  lookahead: number;        // how far ahead we project the racing line
  pitMargin: number;        // multiplier on pit radius for the avoidance corridor
  wallMargin: number;       // px buffer above/below a wall band when sliding past
  basePower: number;        // baseline tilt power
  boostAngleTolerance: number;  // radians from east where we'll still boost
  boostHoldTime: number;    // seconds we wait between boost attempts
  slipstreamHunt: number;   // 0..1 how strongly we hunt to overtake nearby leaders
  jostleAggression: number; // 0..1 willingness to ram an opponent off-line
}> = {
  racer:       { lookahead: 360, pitMargin: 2.0, wallMargin: 4,  basePower: 0.95, boostAngleTolerance: Math.PI / 5,  boostHoldTime: 0.25, slipstreamHunt: 0.55, jostleAggression: 0.6 },
  careful:     { lookahead: 280, pitMargin: 3.0, wallMargin: 12, basePower: 0.80, boostAngleTolerance: Math.PI / 9,  boostHoldTime: 0.55, slipstreamHunt: 0.10, jostleAggression: 0.1 },
  opportunist: { lookahead: 320, pitMargin: 2.4, wallMargin: 8,  basePower: 0.88, boostAngleTolerance: Math.PI / 7,  boostHoldTime: 0.40, slipstreamHunt: 0.35, jostleAggression: 0.35 },
};

function pickPersonality(): Marble['aiPersonality'] {
  const r = Math.random();
  if (r < 0.45) return 'racer';
  if (r < 0.80) return 'opportunist';
  return 'careful';
}

/** Steer a bot marble east using candidate-scoring path-finding.
 *
 *  Every frame we sample N target Y values evenly across the lane
 *  and score each by:
 *    - forward path clearness (walls + pits as sampled obstacles)
 *    - deviation from current Y (smaller is better)
 *    - slipstream hunt (penalty for being directly behind an opponent
 *      if we're a racer, bonus for jostle aggression if we want to
 *      bump them)
 *  Then we steer toward the best scoring Y. No corridor lock-in, no
 *  stuck states — the bot re-evaluates every tick.
 *
 *  Stuck-recovery is aggressive: if speed < 40 px/s for 0.25s and
 *  we're not near the finish, we force a sideways push + boost to
 *  break free. */
function aiSteer(world: World, m: Marble): void {
  const prof = AI_PROFILES[m.aiPersonality];
  const speed = Math.hypot(m.vx, m.vy);
  const dt = 0.016;

  // Stuck timer — accumulate while moving too slowly outside the
  // finish band.
  if (speed < 40 && m.x < FINISH_X - 60) {
    m.aiLastProgressAt = m.aiLastProgressAt + dt;
  } else {
    m.aiLastProgressAt = 0;
    m.aiLastProgressX = m.x;
  }

  // Stuck-state recovery — drive pure-vertical to the nearest wall-free
  // column. Sideways pushes had a fatal bug: they routinely shoved the
  // bot deeper into the wall it was stuck on.
  if (m.aiLastProgressAt > 0.25) {
    const clearY = findCorridorPastNextWall(m, prof.lookahead) ?? m.y;
    const dy = clearY - m.y;
    if (Math.abs(dy) > 8) {
      m.inHeading = dy > 0 ? Math.PI / 2 : -Math.PI / 2;   // straight down / straight up
    } else {
      // Already in a clearing but still not moving — try a backwards
      // hop to reset velocity.
      m.inHeading = Math.PI;   // west
    }
    m.inThrust = true;
    m.inPower = 1;
    if (world.time >= m.boostCdUntil && m.aiLastProgressAt > 0.45) tryBoost(world, m);
    return;
  }

  // Score N candidate Ys spread across the lane height.
  const CANDIDATES = 9;
  let bestY = m.y;
  let bestScore = -Infinity;
  for (let i = 0; i < CANDIDATES; i++) {
    const candY = MARBLE_RADIUS + 6 + (i / (CANDIDATES - 1)) * (ARENA_H - 2 * MARBLE_RADIUS - 12);
    const score = scoreCandidate(world, m, candY, prof, speed);
    if (score > bestScore) { bestScore = score; bestY = candY; }
  }

  // No clear single-segment path? Switch to vertical-first routing —
  // find a clearing at the current x and drive pure-vertical to it.
  // Once we're past the wall, normal east-bound scoring resumes the
  // next frame. This is the fix for lanes 1 and 2 (top spawns) which
  // start ABOVE a top-half wall band and would otherwise have no
  // straight-line path eastward.
  if (bestScore < -300) {
    const clearY = findCorridorPastNextWall(m, prof.lookahead) ?? m.y;
    const dy = clearY - m.y;
    if (Math.abs(dy) > 8) {
      m.inHeading = dy > 0 ? Math.PI / 2 : -Math.PI / 2;
      m.inThrust = true;
      m.inPower = 1;
      return;
    }
  }

  const goalX = Math.min(m.x + prof.lookahead, ARENA_W - MARBLE_RADIUS);
  const goalY = bestY;
  const heading = Math.atan2(goalY - m.y, goalX - m.x);

  const wobble = 0.05 * Math.sin(world.time * 1.3 + m.p * 2.1);
  m.inHeading = heading;
  m.inThrust = true;
  m.inPower = clamp(prof.basePower + wobble, 0.55, 1);

  if (
    world.time >= m.boostCdUntil
    && world.time >= m.boostUntil + prof.boostHoldTime
    && Math.abs(heading) < prof.boostAngleTolerance
    && straightawayClear(m, prof.lookahead)
  ) {
    tryBoost(world, m);
  }
}

/** Find a Y that bypasses the NEXT blocking wall in the marble's
 *  forward path. The old findVerticalClearing only checked walls at
 *  the marble's current x position, which meant for a marble at
 *  (700, 580) approaching wall 2 at x=880 it returned 'all y are
 *  clear' (correct at x=700, useless for clearing wall 2).
 *
 *  Returns null if no walls intersect our forward path. Otherwise
 *  returns the Y the marble should aim at — either above or below
 *  the nearest blocking wall, whichever is closer to the marble's
 *  current Y and is valid (not off-arena, not in a pit). */
function findCorridorPastNextWall(m: Marble, lookahead: number): number | null {
  let nearest: { wall: typeof WALLS[number]; distX: number } | null = null;
  for (const wall of WALLS) {
    // Is the wall ahead within lookahead range?
    const ahead = wall.x + wall.w > m.x && wall.x < m.x + lookahead;
    if (!ahead) continue;
    // Is the marble's Y inside the wall's vertical reach? If we're
    // already above or below the band, this wall doesn't block us.
    const wallTop = wall.y - MARBLE_RADIUS - 6;
    const wallBot = wall.y + wall.h + MARBLE_RADIUS + 6;
    if (m.y < wallTop || m.y > wallBot) continue;
    const distX = wall.x - m.x;
    if (!nearest || distX < nearest.distX) nearest = { wall, distX };
  }
  if (!nearest) return null;
  const wall = nearest.wall;
  const wallTop = wall.y - MARBLE_RADIUS - 8;
  const wallBot = wall.y + wall.h + MARBLE_RADIUS + 8;
  const aboveValid = wallTop > MARBLE_RADIUS + 4;
  const belowValid = wallBot < ARENA_H - MARBLE_RADIUS - 4;
  // Reject corridors that would land in a pit too.
  const inPit = (y: number): boolean => {
    for (const pit of PITS) {
      if (pit.x < m.x || pit.x > m.x + lookahead) continue;
      const dx = pit.x - m.x, dy = pit.y - y;
      const safeR = pit.r + MARBLE_RADIUS + 6;
      if (dx * dx + dy * dy < safeR * safeR) return true;
    }
    return false;
  };
  const aboveOk = aboveValid && !inPit(wallTop);
  const belowOk = belowValid && !inPit(wallBot);
  if (!aboveOk && !belowOk) return null;
  if (!aboveOk) return wallBot;
  if (!belowOk) return wallTop;
  return Math.abs(m.y - wallTop) < Math.abs(m.y - wallBot) ? wallTop : wallBot;
}

/** Evaluate one candidate target Y. Higher score = better choice. */
function scoreCandidate(
  world: World,
  m: Marble,
  candY: number,
  prof: typeof AI_PROFILES[Marble['aiPersonality']],
  speed: number,
): number {
  let score = 0;
  const SAMPLES = 6;
  // Forward path clearness — sample LERP from current to candidate.
  for (let i = 1; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const x = m.x + prof.lookahead * t;
    const y = m.y + (candY - m.y) * t;
    // Wall blocks — hard penalty.
    for (const wall of WALLS) {
      if (x > wall.x - MARBLE_RADIUS && x < wall.x + wall.w + MARBLE_RADIUS
       && y > wall.y - MARBLE_RADIUS && y < wall.y + wall.h + MARBLE_RADIUS) {
        score -= 1000;
      }
    }
    // Pit penalty — proximity-weighted, widened by current speed.
    const speedScale = 1 + Math.min(1, speed / MAX_SPEED) * 0.4;
    for (const pit of PITS) {
      const dx = x - pit.x, dy = y - pit.y;
      const d = Math.hypot(dx, dy);
      const margin = pit.r * prof.pitMargin * speedScale;
      if (d < margin) {
        score -= 250 * (1 - d / margin);
      }
    }
  }
  // Prefer minimum deviation from current Y so the path is smooth.
  score -= Math.abs(candY - m.y) * 0.4;
  // Slipstream / jostle interactions with other marbles.
  for (const other of world.marbles.values()) {
    if (other.p === m.p) continue;
    if (other.finishedAt !== null) continue;
    const dx = other.x - m.x;
    const dyToOther = other.y - candY;
    if (dx <= 0 || dx > prof.lookahead * 0.75) continue;
    if (Math.abs(dyToOther) > 60) continue;
    // Being directly behind a leader is bad for racers (they want to
    // overtake), neutral for careful bots.
    const directlyBehind = Math.abs(dyToOther) < 22;
    if (directlyBehind) score -= 40 * prof.slipstreamHunt;
    // Side-by-side is a jostle opportunity for aggressive bots.
    const sideBySide = dx < prof.lookahead * 0.35 && Math.abs(dyToOther) < 50;
    if (sideBySide) score += 25 * prof.jostleAggression;
  }
  return score;
}

function straightawayClear(m: Marble, lookahead: number): boolean {
  const samples = 6;
  for (let i = 1; i <= samples; i++) {
    const x = m.x + (lookahead / samples) * i;
    const y = m.y;
    for (const pit of PITS) {
      const dx = x - pit.x, dy = y - pit.y;
      if (dx * dx + dy * dy < (pit.r + MARBLE_RADIUS) * (pit.r + MARBLE_RADIUS)) return false;
    }
    for (const wall of WALLS) {
      if (x > wall.x - MARBLE_RADIUS && x < wall.x + wall.w + MARBLE_RADIUS
       && y > wall.y - MARBLE_RADIUS && y < wall.y + wall.h + MARBLE_RADIUS) return false;
    }
  }
  return true;
}

function canPlace(x: number, y: number): boolean {
  if (x < MARBLE_RADIUS || x > ARENA_W - MARBLE_RADIUS) return false;
  if (y < MARBLE_RADIUS || y > ARENA_H - MARBLE_RADIUS) return false;
  for (const wall of WALLS) {
    const cx = Math.max(wall.x, Math.min(x, wall.x + wall.w));
    const cy = Math.max(wall.y, Math.min(y, wall.y + wall.h));
    const dx = x - cx, dy = y - cy;
    if (dx * dx + dy * dy < MARBLE_RADIUS * MARBLE_RADIUS) return false;
  }
  return true;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}
